import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { callAvosApi } from "../../../utils/useAvosApiHook";
import { Signal, SignalsResponse } from "./signalTypes";

export const SIGNALS_KEY = "signals";

const buildQuery = (params: Record<string, any>): string => {
    const entries = Object.entries(params).filter(
        ([, value]) => value !== null && value !== undefined && value !== "",
    );
    return new URLSearchParams(entries as [string, string][]).toString();
};

const POLL_MS = 60000;

/**
 * Did the server give a SETTLED answer — one that repeating the request cannot
 * change?
 *
 * A 4xx is such an answer: the signal does not exist, or this tenant has no
 * signals (`require_signals_enabled` returns 403 on every `/signal` route). A
 * 5xx or a dropped connection is not — the server could not answer *this time*,
 * and a later attempt may well succeed.
 *
 * Deliberately the same test as `isNotFound` in useActiveSignal.ts, and
 * deliberately NOT shared with it: that one guards whether to clear the URL
 * parameter and is pinned by its own tests, and coupling the two would mean a
 * change to either had to be reasoned about twice. Copied, not abstracted.
 *
 * The API client rejects with the raw axios error (constants/apiClient.tsx), so
 * an HTTP failure carries `response.status` and a transport failure carries no
 * `response` at all.
 */
const isSettledFailure = (error: unknown): boolean => {
    const status = (error as { response?: { status?: number } } | null)?.response?.status;
    return typeof status === "number" && status >= 400 && status < 500;
};

/**
 * Retry policy for every polled signals query: keep the app-wide default for
 * failures that might heal, give up immediately on ones that cannot.
 *
 * This is what stops a permanent condition becoming a toast storm.
 * `constants/apiClient.tsx` installs a global response interceptor that raises
 * a toast for EVERY failed request — 403 and 404 included — and toasts carry no
 * `toastId`, so identical messages stack rather than replace. Left at the
 * default, one unavailable endpoint produced four toasts per attempt-burst, and
 * the poll below brought a fresh burst every 60s, for as long as the page
 * stayed open. Closing them could not win.
 *
 * Reporting the condition ONCE is the goal — not suppressing it. Nothing here
 * sets `no_toast`; a genuine failure still surfaces, exactly one time.
 */
const retryUnlessSettled = (failureCount: number, error: unknown): boolean => (
    !isSettledFailure(error) && failureCount < 3
);

/** Stop polling once the answer is settled — repeating it cannot change it, and
 *  each repeat costs another toast. Transient failures keep polling and heal. */
const pollUnlessSettled = (error: unknown): number | false => (
    isSettledFailure(error) ? false : POLL_MS
);

/** List signals (polled every 60s until a settled failure). Filters are part of the query key. */
export const useSignals = (params: Record<string, any>, options: { enabled?: boolean } = {}) => useQuery({
    queryKey: [SIGNALS_KEY, params],
    queryFn: async (): Promise<SignalsResponse> => {
        const { data } = await callAvosApi(`/signal/?${buildQuery(params)}`);
        return data;
    },
    refetchInterval: (query) => pollUnlessSettled(query.state.error),
    retry: retryUnlessSettled,
    enabled: options.enabled ?? true,
});

/** A single signal with its relations, meta, and audit timeline (polled every 60s).
 *
 *  The poll is what lets the active-signal bar notice that a colleague closed
 *  the signal, without the operator clicking anything (D2/D7). It is shared
 *  with SignalDetailModal, so an open detail modal now polls too — accepted:
 *  the interval matches useSignals/useSignalRollup and a modal refreshing its
 *  own timeline is harmless. Not worth a per-caller option for one caller.
 */
export const useSignalDetail = (signalId?: string) => useQuery({
    queryKey: [SIGNALS_KEY, "detail", signalId],
    queryFn: async (): Promise<Signal> => {
        const { data } = await callAvosApi(`/signal/${signalId}`);
        return data;
    },
    refetchInterval: (query) => pollUnlessSettled(query.state.error),
    // Retries keep the app-wide default for failures that MIGHT heal, and stop
    // at the first settled one. The distinction is what D5's amendment turns
    // on: a blip must never be mistaken for "this signal is gone", so a 5xx or
    // a dropped connection still gets its retries and the bar reappears when a
    // later attempt lands.
    //
    // An earlier revision left retries entirely at the default here, arguing a
    // dead `?activeSignal=` id could not poll forever because useActiveSignal
    // clears the parameter once the answer is a 4xx. That is still true, and it
    // was still four toasts before the clear — and it never covered
    // SignalDetailModal, which uses this hook with no parameter to clear.
    retry: retryUnlessSettled,
    enabled: !!signalId,
});

/** Resolve / reject a signal with an optional note; refetches all signal lists. */
export const useUpdateSignal = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ signalId, status, note }: { signalId: string; status: string; note?: string }): Promise<Signal> => {
            const { data } = await callAvosApi(`/signal/${signalId}`, {
                method: "PUT",
                data: { status, note },
            });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [SIGNALS_KEY], exact: false });
        },
    });
};

/** Per-cell traffic-light rollup for the given cells. */
export const useSignalRollup = (cellIds: string[], options: { enabled?: boolean } = {}) => useQuery({
    queryKey: [SIGNALS_KEY, "rollup", cellIds],
    queryFn: async (): Promise<Record<string, "red" | "amber" | "green">> => {
        const { data } = await callAvosApi(`/signal/rollup?facility_location_id=${cellIds.join(",")}`);
        return data;
    },
    refetchInterval: (query) => pollUnlessSettled(query.state.error),
    retry: retryUnlessSettled,
    enabled: (options.enabled ?? true) && cellIds.length > 0,
});
